import { Form, Input, Button, Modal } from 'antd';
import { UseMutationResult } from '@tanstack/react-query';
import { MIN_10_WORDS_REGEX } from 'src/constants';
import { useEffect } from 'react';

interface ReceptionistCancelReasonModalProps {
	visible: boolean;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	loading?: boolean;
	pausePayment?: boolean;
}

const ReceptionistCancelReasonModal = ({
	visible,
	mutation,
	loading = false,
	pausePayment = false,
}: ReceptionistCancelReasonModalProps) => {
	const [form] = Form.useForm<{ reason: string }>();

	const handleSubmit = async (values: { reason: string }) => {
		await mutation.mutateAsync({
			reason: values.reason?.trim(),
			subscriptionType: 'Inbound',
		});
	};

	useEffect(() => {
		if (!visible) {
			form.resetFields();
		}
	}, [visible, form]);

	return (
		<Modal
			title={`${pausePayment ? 'Pause Subscription' : 'Cancel Subscription'}`}
			open={visible}
			footer={null}
			width={600}
			destroyOnHidden
			maskClosable={false}
			closable={false}
			keyboard={false}
		>
			<Form form={form} onFinish={handleSubmit} layout="vertical" name="cancel-sub-reason-form">
				<div className="mb-4">
					<p className="text-gray-600 mb-4">
						{pausePayment
							? 'We are sorry to see you go. To help us improve our service, please let us know why you are pausing your subscription.'
							: "We're sorry to see you go. To help us improve our service, please let us know why you're cancelling your subscription."}
					</p>
				</div>

				<Form.Item
					required
					label="What was our product missing?"
					name="reason"
					rules={[
						{ required: true, message: 'Please input your reason!' },
						{
							pattern: MIN_10_WORDS_REGEX,
							message: 'Please enter at least 10 words',
							transform: (value) => value.trim(),
						},
					]}
				>
					<Input.TextArea
						placeholder="Please share your feedback so we can improve"
						rows={4}
						showCount
						maxLength={500}
						onChange={(e) => {
							e.target.value = e.target.value.trim();
						}}
					/>
				</Form.Item>

				<div className="mt-6 flex justify-end">
					<Button type="primary" htmlType="submit" loading={loading || mutation.isPending}>
						Submit
					</Button>
				</div>
			</Form>

			<div className="mt-4 pt-3 text-center text-gray-500 text-sm border-t border-gray-200">
				Your feedback helps us improve our product for everyone.
			</div>
		</Modal>
	);
};

export default ReceptionistCancelReasonModal;
